import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  useLoad,
  useTriggerLoad,
  useTriggerLoadWithInput,
} from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import {
  FulfillmentGroupData,
  FulfillmentGroupPickStatus,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  expandLineItemsToPickableItems,
  getPickedMapByExternalId,
} from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { RedoFulfillmentTote } from "@redotech/redo-fulfillment-group-common/fulfillment-tote";
import { PickSessionStrategy } from "@redotech/redo-fulfillment-wms-common/pick-session-enums";
import { isWmsSotEnabledForLocation } from "@redotech/redo-fulfillment-wms-common/wms-constants";
import { useTranslation } from "@redotech/redo-i18n";
import AlertHexagonIcon from "@redotech/redo-icons/alert-hexagon_filled.svg";
import ScanIcon from "@redotech/redo-icons/scan_filled.svg";
import {
  inventoryItemIdFormat,
  LocationId,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ProductId, productIdFormat } from "@redotech/redo-product-id";
import { type StorageLocationId } from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoStandaloneTable } from "@redotech/redo-web/arbiter-components/tables/redo-standalone-table";
import { RedoTableColumn } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo, useState } from "react";
import { getDeviceLocationId } from "../../hooks/use-device-location";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { BinQuantityModal } from "../../wms/pick-sessions/bin-quantity-modal";
import {
  CancelItemModal,
  createBinLocationColumn,
  createCancelItemColumn,
  getCancelItemDisabledMessage,
  type ProblemItemRow,
  useCancelUnpickedItem,
} from "../../wms/pick-sessions/problem-item-actions";
import type { ProblemPickSource } from "../../wms/pick-sessions/problem-pick-source-allocation";
import {
  buildProblemPickQuantityUpdates,
  groupProblemPickLineItems,
} from "../../wms/pick-sessions/problem-pick-source-allocation";
import { ProductBinsModal } from "../../wms/transfer/product-bins-modal";

type ItemTableRow = ProblemItemRow;

const createItemColumn = (): RedoTableColumn<ItemTableRow> => ({
  key: "item",
  widthMode: { type: "fill", minLength: "200px" },
  renderHeaderCell: () => StandardRedoTableHeaderCell({ headerText: "Item" }),
  renderNormalCell: ({ row }) => (
    <Flex align="center" gap="md" px="xl" py="sm">
      {row.imageUrl && (
        <img
          alt={row.productTitle}
          src={row.imageUrl}
          style={{
            width: "32px",
            height: "32px",
            objectFit: "cover",
            borderRadius: "var(--border-radius-sm)",
          }}
        />
      )}
      <Flex dir="column" gap="xxs">
        <Text fontSize="xs" fontWeight="medium">
          {row.variantTitle !== "Default Title"
            ? `${row.productTitle} - ${row.variantTitle}`
            : row.productTitle}
        </Text>
        <Text fontSize="xs" textColor="secondary">
          SKU: {row.sku}
        </Text>
      </Flex>
    </Flex>
  ),
});

const createMissingColumn = (): RedoTableColumn<ItemTableRow> => ({
  key: "missing",
  widthMode: { type: "fixed", width: "80px" },
  renderHeaderCell: () =>
    StandardRedoTableHeaderCell({ headerText: "Missing" }),
  renderNormalCell: ({ row }) => (
    <Flex
      align="center"
      justify="center"
      p="sm"
      style={{
        backgroundColor: "var(--redo-colors-background-bg-error-primary)",
      }}
      w="full"
    >
      <RedoTableTextCell
        fontWeight="semibold"
        text={String(row.quantity - row.pickedQuantity)}
        textColor="error"
      />
    </Flex>
  ),
  alignment: "center",
});

const createMarkAsPickedColumn = (
  onMarkAsPicked: (row: ItemTableRow) => void,
  disabled?: boolean,
): RedoTableColumn<ItemTableRow> => ({
  key: "markAsPicked",
  widthMode: { type: "fixed", width: "130px" },
  renderHeaderCell: () => StandardRedoTableHeaderCell({ headerText: "" }),
  renderNormalCell: ({ row }) => (
    <Flex align="center" justify="center" p="sm">
      <RedoButton
        disabled={disabled}
        hierarchy="tertiary"
        onClick={() => onMarkAsPicked(row)}
        size="xs"
        text={disabled ? "Assign tote first" : "Mark as picked..."}
        theme="normal"
      />
    </Flex>
  ),
  alignment: "center",
});

interface MissingItemsModalProps {
  isOpen: boolean;
  fulfillmentGroup: FulfillmentGroupData;
  onClose: () => void;
  onConfirm: (andFulfill: boolean) => void;
  onViewFullDetails: () => void;
  /** Omitted when the modal is already on this group's scan page. */
  onScanAndVerify?: () => void;
  onFulfillmentGroupUpdated?: (group: FulfillmentGroupData) => void;
  canScanAndVerify: boolean;
}

export const MissingItemsModal = memo(function MissingItemsModal({
  isOpen,
  fulfillmentGroup,
  onClose,
  onConfirm,
  onViewFullDetails,
  onScanAndVerify,
  onFulfillmentGroupUpdated,
  canScanAndVerify,
}: MissingItemsModalProps) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const { t } = useTranslation();
  const team = useContext(TeamContext);
  const canEditOrder = useHasPermission(OrderManagementPermissions.EDIT_ORDER);
  const isMobile = useScreenType() === ScreenType.MOBILE;

  const [isBinQuantityModalOpen, setIsBinQuantityModalOpen] = useState(false);
  const [cancelItemRow, setCancelItemRow] = useState<ItemTableRow | null>(null);
  const [viewBinsRow, setViewBinsRow] = useState<ItemTableRow | null>(null);
  const [binsRefreshKey, setBinsRefreshKey] = useState(0);
  const [initialSourceBinId, setInitialSourceBinId] =
    useState<StorageLocationId | null>(null);
  const [selectedItem, setSelectedItem] = useState<ItemTableRow | null>(null);
  const [quantityLeftToPick, setQuantityLeftToPick] = useState(0);
  const [pickSessionReloadKey, setPickSessionReloadKey] = useState(0);

  const pickSessionLoad = useLoad(
    async (signal) => {
      if (!fulfillmentGroup?.pickSessionId) {
        return null;
      }
      return await rpcClient.getPickSession(
        { id: fulfillmentGroup.pickSessionId },
        { signal },
      );
    },
    [rpcClient, fulfillmentGroup?.pickSessionId, pickSessionReloadKey],
  );

  const pickSession = pickSessionLoad.value;

  const wmsSotEnabled = isWmsSotEnabledForLocation(
    team?.settings?.outboundLabels?.wms,
    pickSession?.crdbLocationId,
  );
  const requireSourceBin = !!(wmsSotEnabled && pickSession?.crdbLocationId);

  const hasToteAssigned = useMemo(() => {
    if (!pickSession) {
      return false;
    }
    return pickSession.items.some(
      (item) =>
        item.fulfillmentGroupId === fulfillmentGroup._id &&
        item.quantityByTote &&
        Object.keys(item.quantityByTote).length > 0,
    );
  }, [pickSession, fulfillmentGroup._id]);

  const canAssignTote = requireSourceBin && !hasToteAssigned;
  // Single-item problem picks can go straight to a transit bin instead.
  const needsToteAssignment =
    canAssignTote && pickSession?.strategy !== PickSessionStrategy.SINGLE_ITEM;

  const [selectedTote, setSelectedTote] =
    useState<RedoListItem<RedoFulfillmentTote> | null>(null);
  const [toteSearchString, setToteSearchString] = useState("");

  const availableTotesLoad = useLoad(
    async (signal) => {
      if (!canAssignTote) {
        return [];
      }
      const result = await rpcClient.getTotes(
        { locationId: getDeviceLocationId() },
        { signal },
      );
      return result.totes || [];
    },
    [rpcClient, canAssignTote],
  );

  const toteOptions: RedoListItem<RedoFulfillmentTote>[] = useMemo(() => {
    if (!availableTotesLoad.value) {
      return [];
    }
    const currentPickSessionId = pickSession?._id?.toString();
    const filtered = toteSearchString
      ? availableTotesLoad.value.filter((t) =>
          t.name.toLowerCase().includes(toteSearchString.toLowerCase()),
        )
      : availableTotesLoad.value;
    return filtered
      .filter(
        (t) => !t.pickSessionId || t.pickSessionId === currentPickSessionId,
      )
      .map((tote) => ({
        type: "text" as const,
        id: tote._id.toString(),
        value: tote,
        text: tote.name,
      }));
  }, [availableTotesLoad.value, toteSearchString, pickSession]);

  const [assignToteLoad, triggerAssignTote] = useTriggerLoad(async (signal) => {
    if (!selectedTote || !fulfillmentGroup._id) {
      return;
    }
    try {
      const result = await rpcClient.moveFulfillmentGroupToTotes(
        {
          fulfillmentGroupId: fulfillmentGroup._id,
          toteIds: [selectedTote.value._id.toString()],
        },
        { signal },
      );
      if (!result.success) {
        toast(result.message || "Failed to assign tote", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 3 }),
        });
        return;
      }
      // Reload pick session to update hasToteAssigned
      setPickSessionReloadKey((k) => k + 1);
      setSelectedTote(null);
      setToteSearchString("");
    } catch (error) {
      toast("Failed to assign tote", {
        variant: "error",
        duration: Temporal.Duration.from({ seconds: 3 }),
      });
    }
  });

  const showCustomAttributes =
    !!team?.settings?.outboundLabels?.degroupItemsByCustomAttributes;

  const pickedQuantityByExternalId = getPickedMapByExternalId(fulfillmentGroup);

  const unpickedTableData: ItemTableRow[] = useMemo(() => {
    if (fulfillmentGroup.pickStatus !== FulfillmentGroupPickStatus.PROBLEM) {
      return [];
    }

    const expandedItems = expandLineItemsToPickableItems(
      fulfillmentGroup.items,
      {
        hideInputItems:
          team?.settings.outboundLabels?.billOfMaterials
            ?.alwaysHideInputItems ?? false,
      },
    );
    const itemsWithPickedQty = groupProblemPickLineItems({
      items: expandedItems,
      pickedQuantityByExternalId,
      degroupByCustomAttributes: showCustomAttributes,
    }).map(({ item, originalItems, pickedQuantity }) => ({
      ...item,
      pickedQuantity,
      allOriginalItems: originalItems,
    }));

    return itemsWithPickedQty.filter(
      (item) => item.pickedQuantity < item.quantity,
    );
  }, [
    fulfillmentGroup.items,
    fulfillmentGroup.pickStatus,
    pickedQuantityByExternalId,
    showCustomAttributes,
    team?.settings.outboundLabels?.billOfMaterials?.alwaysHideInputItems,
  ]);

  const handleBinQuantityModalClose = useHandler(() => {
    setSelectedItem(null);
    setIsBinQuantityModalOpen(false);
    setQuantityLeftToPick(0);
    setInitialSourceBinId(null);
  });

  const handleMarkAsPicked = useHandler((row: ItemTableRow) => {
    setSelectedItem(row);
    setIsBinQuantityModalOpen(true);
    setQuantityLeftToPick(row.quantity - row.pickedQuantity);
    setInitialSourceBinId(null);
  });

  const handlePickFromBin = useHandler(
    (row: ItemTableRow, storageLocationId: StorageLocationId) => {
      handleMarkAsPicked(row);
      setInitialSourceBinId(storageLocationId);
    },
  );

  const binsLocationId = requireSourceBin
    ? locationIdFormat.schema.safeParse(pickSession?.crdbLocationId).data
    : undefined;
  const pickFromBinDisabledMessage = needsToteAssignment
    ? t("Assign a tote before marking items as picked")
    : undefined;

  const [quantityUpdateLoad, triggerQuantityUpdate] = useTriggerLoadWithInput(
    async (
      input: { quantity: number; sources?: ProblemPickSource[] },
      signal,
    ) => {
      if (!selectedItem || !fulfillmentGroup?._id) {
        return;
      }

      try {
        const updates = buildProblemPickQuantityUpdates({
          originalItems: selectedItem.allOriginalItems,
          pickedQuantityByExternalId,
          quantity: input.quantity,
          sources: input.sources,
        });

        if (updates.length > 0) {
          const result = await rpcClient.setFulfillmentGroupPickedQuantities(
            { fulfillmentGroupId: fulfillmentGroup._id, updates },
            { signal },
          );

          if (!result.success) {
            toast(result.message || "Failed to update quantity", {
              variant: "error",
              duration: Temporal.Duration.from({ seconds: 3 }),
            });
            return;
          }

          if (result.pickSessionItemStatuses && onFulfillmentGroupUpdated) {
            const updatedGroup = {
              ...fulfillmentGroup,
              pickSessionItemStatuses: result.pickSessionItemStatuses,
            };
            onFulfillmentGroupUpdated(updatedGroup);
          }
        }
      } catch (error) {
        toast("Failed to update quantity", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 3 }),
        });
      } finally {
        handleBinQuantityModalClose();
      }
    },
  );

  const refreshFulfillmentGroup = useHandler(async () => {
    try {
      const { fulfillmentGroup: updatedGroup } =
        await rpcClient.getLatestOpenFulfillmentGroup({
          fulfillmentGroupId: fulfillmentGroup._id,
        });
      onFulfillmentGroupUpdated?.(updatedGroup);
    } catch {
      // The cancel succeeded; a stale row only lasts until the modal reopens.
    }
  });

  const [cancelItemLoad, triggerCancelItem] = useCancelUnpickedItem({
    fulfillmentGroupId: fulfillmentGroup._id,
    pickedQuantityByExternalId,
    onSettled: () => setCancelItemRow(null),
    onCancelled: () => void refreshFulfillmentGroup(),
  });

  const columns: RedoTableColumn<ItemTableRow>[] = useMemo(
    () => [
      createItemColumn(),
      createBinLocationColumn<ItemTableRow>(
        t,
        binsLocationId
          ? {
              locationId: binsLocationId,
              onPickFromBin: handlePickFromBin,
              onViewAllBins: setViewBinsRow,
              pickDisabledMessage: pickFromBinDisabledMessage,
              binsRefreshKey,
            }
          : undefined,
      ),
      createMissingColumn(),
      createMarkAsPickedColumn(handleMarkAsPicked, needsToteAssignment),
      createCancelItemColumn<ItemTableRow>(t, setCancelItemRow, (row) =>
        getCancelItemDisabledMessage({
          t,
          canEditOrder,
          fulfillmentGroup,
          row,
        }),
      ),
    ],
    [
      t,
      binsLocationId,
      handlePickFromBin,
      pickFromBinDisabledMessage,
      binsRefreshKey,
      handleMarkAsPicked,
      needsToteAssignment,
      canEditOrder,
      fulfillmentGroup,
    ],
  );

  const noUnpickedItems = unpickedTableData.length === 0;

  const viewBinsProductId = productIdFormat.schema.safeParse(
    viewBinsRow?.redoProductId,
  ).data;

  const [setPickStatusLoad, triggerSetPickStatus] = useTriggerLoadWithInput(
    async (andFulfill: boolean, signal) => {
      try {
        const result = await rpcClient.setFulfillmentGroupPickStatus(
          {
            fulfillmentGroupId: fulfillmentGroup._id,
            pickStatus: FulfillmentGroupPickStatus.PICKED,
          },
          { signal },
        );

        if (!result.success) {
          toast("Failed to mark order as picked", {
            variant: "error",
            duration: Temporal.Duration.from({ seconds: 3 }),
          });
          return;
        }

        if (onFulfillmentGroupUpdated) {
          onFulfillmentGroupUpdated({
            ...fulfillmentGroup,
            pickStatus: FulfillmentGroupPickStatus.PICKED,
          });
        }

        onConfirm(andFulfill);
      } catch (error) {
        toast("Failed to mark order as picked", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 3 }),
        });
      }
    },
  );

  return (
    <>
      <RedoModal
        hideCloseButton={setPickStatusLoad.pending}
        hideFooter
        isOpen={isOpen}
        modalSize="lg"
        noPadding
        onModalCloseRequested={() => {
          if (!setPickStatusLoad.pending) {
            onClose();
          }
        }}
        theme="error"
        title="Missing items"
        TitleIcon={AlertHexagonIcon}
      >
        <Flex dir="column" gap="lg" pb="xs" px="3xl">
          <Text fontSize="sm" textColor="secondary">
            This order has missing items. Once all of the items are picked this
            order may be fulfilled.
          </Text>

          {canAssignTote && (
            <Flex
              align="flex-end"
              bgColor="secondary"
              borderRadius="lg"
              dir="column"
              gap="md"
              p="xl"
            >
              <Text fontSize="sm" fontWeight="medium">
                {needsToteAssignment
                  ? "Assign a tote before marking items as picked"
                  : "Optionally assign a tote. Without one, the picked item is held for this order until it ships."}
              </Text>
              <Flex align="flex-end" gap="md" w="full">
                <Flex grow="1">
                  <RedoSingleSelectDropdownInput
                    emptyListMessage="No totes available"
                    itemsLoading={availableTotesLoad.pending}
                    options={toteOptions}
                    optionSelected={({ item }) => setSelectedTote(item)}
                    placeholder="Select a tote..."
                    searchPlaceholder="Search totes..."
                    searchString={toteSearchString}
                    selectedItem={selectedTote ?? undefined}
                    setSearchString={setToteSearchString}
                    size="sm"
                  />
                </Flex>
                <RedoButton
                  disabled={!selectedTote}
                  hierarchy="primary"
                  onClick={() => triggerAssignTote()}
                  pending={assignToteLoad.pending}
                  size="sm"
                  text="Assign tote"
                />
              </Flex>
            </Flex>
          )}

          {unpickedTableData.length > 0 && (
            <RedoStandaloneTable
              layout={{ columns, headerColor: "grey" }}
              tableData={unpickedTableData}
            />
          )}

          {noUnpickedItems && (
            <Flex
              align="center"
              bgColor="secondary"
              borderRadius="lg"
              justify="center"
              p="xl"
            >
              <Text fontSize="sm" textColor="secondary">
                All items have been picked.
              </Text>
            </Flex>
          )}
        </Flex>
        <Flex
          align={isMobile ? "stretch" : "center"}
          dir={isMobile ? "column" : "row"}
          gap="xl"
          justify="space-between"
          p="3xl"
        >
          <Flex dir={isMobile ? "column" : "row"} gap="sm">
            <RedoButton
              disabled={setPickStatusLoad.pending}
              hierarchy="tertiary"
              onClick={onViewFullDetails}
              size="sm"
              text="View full order details"
            />
            {onScanAndVerify && (
              <RedoButton
                disabled={setPickStatusLoad.pending}
                hierarchy="tertiary"
                IconLeading={ScanIcon}
                onClick={onScanAndVerify}
                size="sm"
                text={t("Scan and verify")}
              />
            )}
          </Flex>
          <Flex dir={isMobile ? "column" : "row"} gap="xl">
            <RedoButton
              disabled={setPickStatusLoad.pending}
              hierarchy="secondary"
              onClick={onClose}
              size="sm"
              text="Cancel"
            />
            <RedoButton
              disabled={!noUnpickedItems || setPickStatusLoad.pending}
              hierarchy={canScanAndVerify ? "secondary" : "primary"}
              onClick={() => triggerSetPickStatus(false)}
              pending={setPickStatusLoad.pending}
              size="sm"
              text="Mark as picked"
              theme="success"
            />
            {canScanAndVerify && (
              <RedoButton
                disabled={!noUnpickedItems || setPickStatusLoad.pending}
                hierarchy="primary"
                onClick={() => triggerSetPickStatus(true)}
                pending={setPickStatusLoad.pending}
                size="sm"
                text={t("Marked As Picked and Fulfill")}
                theme="success"
              />
            )}
          </Flex>
        </Flex>
      </RedoModal>

      {selectedItem && (
        <BinQuantityModal
          initialStorageLocationId={initialSourceBinId}
          isOpen={isBinQuantityModalOpen}
          item={selectedItem}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          locationId={pickSession?.crdbLocationId as LocationId | undefined}
          onClose={handleBinQuantityModalClose}
          onConfirm={(quantity: number, sources?: ProblemPickSource[]) => {
            triggerQuantityUpdate({ quantity, sources });
          }}
          onConfirmLoad={quantityUpdateLoad}
          productId={
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            selectedItem.allOriginalItems[0]?.redoProductId as
              | ProductId
              | undefined
          }
          quantityLeftToPick={quantityLeftToPick}
          requiredInventoryItemId={selectedItem.imsInventoryItemId}
          requireSourceBin={requireSourceBin}
          resetScanValue={() => {}}
          scanValue={null}
          team={team}
        />
      )}

      {cancelItemRow && (
        <CancelItemModal
          crdbLocationId={pickSession?.crdbLocationId}
          defaultBinName={cancelItemRow.bin}
          loading={cancelItemLoad.pending}
          onClose={() => setCancelItemRow(null)}
          onConfirm={(options) =>
            triggerCancelItem({ row: cancelItemRow, ...options })
          }
          productTitle={cancelItemRow.productTitle}
          quantity={cancelItemRow.quantity - cancelItemRow.pickedQuantity}
          redoProductId={cancelItemRow.redoProductId}
          wmsSot={wmsSotEnabled}
        />
      )}

      {viewBinsRow && viewBinsProductId && binsLocationId && (
        <ProductBinsModal
          allowAdjustInventory
          currentBinId={null}
          imageUrl={viewBinsRow.imageUrl ?? null}
          inventoryItemId={
            inventoryItemIdFormat.schema.safeParse(
              viewBinsRow.imsInventoryItemId,
            ).data ?? null
          }
          isOpen
          locationId={binsLocationId}
          lotNumber={null}
          onClose={() => setViewBinsRow(null)}
          onInventoryAdjusted={() => setBinsRefreshKey((key) => key + 1)}
          onSelectBin={(bin) => {
            setViewBinsRow(null);
            if (!pickFromBinDisabledMessage) {
              handlePickFromBin(viewBinsRow, bin.id);
            }
          }}
          productId={viewBinsProductId}
          productTitle={viewBinsRow.productTitle}
          purpose={pickFromBinDisabledMessage ? "view" : "pick"}
          sku={viewBinsRow.sku ?? null}
        />
      )}
    </>
  );
});
